import { z } from 'zod';
import {
  DocumentMetadataSchema,
  FieldKeySchema,
  IdSchema,
  ScalarValueSchema,
  SchemaVersionSchema,
  SortSchema,
  TimeGrainSchema,
} from './common.js';
import { VisualizationSpecSchema } from './visualization.js';

/** Dimension incluida en el analisis; `key` es su nombre local dentro de la consulta. */
export const AnalysisDimensionSchema = z.strictObject({
  key: FieldKeySchema,
  dimensionId: IdSchema,
  timeGrain: TimeGrainSchema.optional(),
  label: z.string().min(1).max(200).optional(),
});
export type AnalysisDimension = z.infer<typeof AnalysisDimensionSchema>;

export const AnalysisMeasureSchema = z.strictObject({
  key: FieldKeySchema,
  measureId: IdSchema,
  label: z.string().min(1).max(200).optional(),
});
export type AnalysisMeasure = z.infer<typeof AnalysisMeasureSchema>;

export const FILTER_OPERATORS = [
  'eq',
  'neq',
  'in',
  'not-in',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'contains',
  'starts-with',
  'ends-with',
  'is-null',
  'is-not-null',
] as const;
export const FilterOperatorSchema = z.enum(FILTER_OPERATORS);
export type FilterOperator = z.infer<typeof FilterOperatorSchema>;

/** Numero de valores que admite cada operador de filtro: [minimo, maximo]. */
export const FILTER_OPERATOR_ARITY: Readonly<Record<FilterOperator, readonly [number, number]>> = {
  eq: [1, 1],
  neq: [1, 1],
  gt: [1, 1],
  gte: [1, 1],
  lt: [1, 1],
  lte: [1, 1],
  contains: [1, 1],
  'starts-with': [1, 1],
  'ends-with': [1, 1],
  between: [2, 2],
  in: [1, Number.POSITIVE_INFINITY],
  'not-in': [1, Number.POSITIVE_INFINITY],
  'is-null': [0, 0],
  'is-not-null': [0, 0],
};

export const FilterTargetSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('dimension'),
    dimensionId: IdSchema,
    timeGrain: TimeGrainSchema.optional(),
  }),
  z.strictObject({ kind: z.literal('measure'), measureId: IdSchema }),
]);
export type FilterTarget = z.infer<typeof FilterTargetSchema>;

/** Filtro sobre una dimension (antes de agregar) o sobre una medida (despues de agregar). */
export const FilterSchema = z.strictObject({
  id: IdSchema,
  target: FilterTargetSchema,
  operator: FilterOperatorSchema,
  values: z.array(ScalarValueSchema).default([]),
});
export type Filter = z.infer<typeof FilterSchema>;
export type FilterInput = z.input<typeof FilterSchema>;

export const GroupingSchema = z.strictObject({
  grandTotal: z.boolean().default(false),
  subtotals: z.boolean().default(false),
  /** Dimensiones (por clave) sobre las que se calculan subtotales. */
  rollupKeys: z.array(FieldKeySchema).default([]),
});
export type Grouping = z.infer<typeof GroupingSchema>;

/** Posicion actual de drill-down dentro de una jerarquia. */
export const DrilldownSchema = z.strictObject({
  hierarchyId: IdSchema,
  /** Nivel que se muestra actualmente. */
  levelId: IdSchema,
  /** Valores elegidos en los niveles superiores. */
  path: z.array(z.strictObject({ levelId: IdSchema, value: ScalarValueSchema })).default([]),
});
export type Drilldown = z.infer<typeof DrilldownSchema>;

export const ComparisonSchema = z.strictObject({
  kind: z.enum(['previous-period', 'same-period-previous-year', 'custom-offset']),
  output: z.enum(['value', 'difference', 'percent-change']),
  /** Dimension temporal de referencia; si se omite se usa la fecha principal del modelo. */
  dateDimensionId: IdSchema.optional(),
  /** Solo para `custom-offset`. */
  offset: z
    .strictObject({ grain: TimeGrainSchema, periods: z.number().int().min(1).max(1000) })
    .optional(),
  /** Medidas comparadas (por clave); vacio = todas. */
  measureKeys: z.array(FieldKeySchema).default([]),
});
export type Comparison = z.infer<typeof ComparisonSchema>;

/** Seleccion asociativa: valores elegidos por el usuario que propagan el contexto. */
export const SelectionSchema = z.strictObject({
  dimensionId: IdSchema,
  values: z.array(ScalarValueSchema).min(1),
});
export type Selection = z.infer<typeof SelectionSchema>;

export const InteractionSchema = z.strictObject({
  selectionMode: z.enum(['single', 'multiple']).default('multiple'),
  crossFilter: z.boolean().default(true),
  drillMode: z.enum(['hierarchy', 'none']).default('hierarchy'),
});
export type Interaction = z.infer<typeof InteractionSchema>;

export const LayoutSchema = z.strictObject({
  title: z.string().max(200).optional(),
  subtitle: z.string().max(400).optional(),
  /** Posicion en una cuadricula de workbook/dashboard. */
  position: z
    .strictObject({
      x: z.number().int().nonnegative(),
      y: z.number().int().nonnegative(),
      w: z.number().int().positive(),
      h: z.number().int().positive(),
    })
    .optional(),
  density: z.enum(['compact', 'comfortable']).default('comfortable'),
  showFilterBar: z.boolean().default(true),
});
export type Layout = z.infer<typeof LayoutSchema>;

export const MAX_QUERY_LIMIT = 1_000_000;
export const QueryLimitSchema = z.number().int().positive().max(MAX_QUERY_LIMIT);

/**
 * Estado de un analisis: QUE se esta analizando. Referencia al Semantic Model solo
 * por identificadores y nunca lo modifica. Es serializable (guardar, compartir,
 * reproducir, undo/redo) y no contiene filas, resultados, SQL ni componentes.
 */
export const AnalysisStateSchema = z.strictObject({
  schemaVersion: SchemaVersionSchema,
  id: IdSchema,
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).optional(),
  semanticModelId: IdSchema,
  dimensions: z.array(AnalysisDimensionSchema).default([]),
  measures: z.array(AnalysisMeasureSchema).default([]),
  filters: z.array(FilterSchema).default([]),
  grouping: GroupingSchema.prefault({}),
  sorting: z.array(SortSchema).default([]),
  limit: QueryLimitSchema.nullable().default(null),
  drilldown: z.array(DrilldownSchema).default([]),
  comparison: ComparisonSchema.nullable().default(null),
  selections: z.array(SelectionSchema).default([]),
  visualization: VisualizationSpecSchema.prefault({ type: 'table' }),
  interaction: InteractionSchema.prefault({}),
  layout: LayoutSchema.prefault({}),
  metadata: DocumentMetadataSchema.optional(),
});
export type AnalysisState = z.infer<typeof AnalysisStateSchema>;
export type AnalysisStateInput = z.input<typeof AnalysisStateSchema>;
